import { transferRemote } from "../i18n/transferFeedbackPresentation";
import { useTransfersLanguage, transferLocal, transferFailure, transferMessage, type TransferFeedback } from "../i18n/transfersPresentation";
/**
 * useSetInstanceDashboardUrl / useAdvertisedDashboardUrl / fireAutoAdvertise
 *
 * THIS instance advertises its OWN dashboard base URL so a same-owner admin can
 * click a peer in the transfer cockpit/picker and land on that peer's transfers
 * page (the peer's own login gates it — no cross-server trust). Two ways the box
 * can advertise, one source of truth on the server:
 *   - AUTO (default on): the box publishes its own reachable origin, resolved from
 *     the browser's `window.location.origin` at load time (see `fireAutoAdvertise`).
 *   - MANUAL override: an admin sets a custom dashboard URL (e.g. behind a reverse
 *     proxy) that always wins over auto.
 *
 * - Write: `PUT /api/transfer/instances/dashboard-url` (admin only). A PARTIAL
 *   update — send only the keys that changed (`dashboardUrl`, `autoAdvertise`),
 *   plus `origin` so a "clear the override while auto is on" resolves to the box
 *   origin in one shot (no flap). `dashboardUrl: ""` clears the override. The
 *   response carries the resulting `{ dashboardUrl, autoAdvertise, effectiveSource }`
 *   and an optional soft `warning` when the value was saved locally but the broker
 *   advertise did not go through yet (the admin can retry).
 * - Read-back: `GET /api/transfer/instances/self` (admin only) →
 *   `{ data: { dashboardUrl, autoAdvertise, effectiveSource } }`, used to prefill
 *   the settings input, the toggle, and the "current state" label.
 *
 * This is a DASHBOARD address, never the opaque data-leg endpoint.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfToken } from "../lib/csrf";

/**
 * Max stored dashboard-URL length, mirrored from the backend contract
 * `DASHBOARD_URL_MAX` (`src/lib/contracts/transfer.ts`). Kept in-app because the
 * web tsconfig only includes `apps/web/src`; the server Zod re-validates and is
 * authoritative. The value is trimmed then must be an absolute http(s) URL with a
 * host and no embedded credentials (empty = clear).
 */
export const DASHBOARD_URL_MAX = 512;

/**
 * Which policy branch decides what this box currently advertises: a manual custom
 * URL, its own auto-resolved origin, or nothing. Mirrors the server enum
 * `DashboardAdvertiseSourceSchema`; drives the settings "current state" label.
 */
export type DashboardAdvertiseSource = "manual" | "auto" | "off";

/** The persisted advertise state read back from `GET .../instances/self`. */
export type InstanceAdvertiseState = {
  /** The manual custom-URL override (null/empty = no override; auto may still run). */
  dashboardUrl: string | null;
  /** Whether auto-advertise of the box origin is enabled (server default TRUE). */
  autoAdvertise: boolean;
  /** The effective policy branch in force right now. */
  effectiveSource: DashboardAdvertiseSource;
};

/** Partial settings update — omit a key to leave it unchanged. */
export type SetDashboardUrlInput = {
  /** New manual override; "" clears it. Omit to leave the override unchanged. */
  dashboardUrl?: string;
  /** New auto-advertise flag. Omit to leave it unchanged. */
  autoAdvertise?: boolean;
  /** This box's own origin, so a save that resolves to auto advertises in one shot. */
  origin?: string;
};

export type SetInstanceDashboardUrlResult =
  | { ok: true; data?: InstanceAdvertiseState; warning?: string }
  | { ok: false; message: string; messageFeedback?: TransferFeedback };

/**
 * Fire-and-forget POST of this box's own origin to the auto-advertise endpoint.
 * The server is idempotent (it only pushes to the broker when the effective value
 * changed) and degrades a loopback/garbage origin to "not advertised" rather than
 * an error, so an unconditional fire is always safe. Any failure is swallowed —
 * advertising is best-effort, and the deep-link simply degrades to non-clickable
 * for that peer, never an error surfaced to the admin. Callers do NOT await it.
 */
export const fireAutoAdvertise = (): void => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getCsrfToken();
  if (token) headers["x-csrf-token"] = token;
  void fetch("/api/transfer/instances/dashboard-url/auto", {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ origin: window.location.origin }),
  }).catch(() => {
    // Best-effort — never surface. The next page load retries.
  });
};

type UseSetInstanceDashboardUrlReturn = {
  saving: boolean;
  setDashboardUrl: (input: SetDashboardUrlInput) => Promise<SetInstanceDashboardUrlResult>;
};

type SelfDataDto = {
  dashboardUrl: string | null;
  autoAdvertise?: boolean;
  effectiveSource?: DashboardAdvertiseSource;
};

export const useSetInstanceDashboardUrl = (): UseSetInstanceDashboardUrlReturn => {
  useTransfersLanguage();
  const [saving, setSaving] = useState(false);

  const setDashboardUrl = useCallback(
    async (input: SetDashboardUrlInput): Promise<SetInstanceDashboardUrlResult> => {
      setSaving(true);
      try {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const token = getCsrfToken();
        if (token) headers["x-csrf-token"] = token;

        const res = await fetch("/api/transfer/instances/dashboard-url", {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify(input),
        });
        const json = (await res.json().catch(() => null)) as
          | { ok: true; data?: SelfDataDto; warning?: string }
          | { ok: false; message?: string; messageFeedback?: TransferFeedback }
          | null;
        if (!res.ok || !json?.ok) {
          const message = transferRemote(json) || `HTTP ${res.status}`;
          return { ...transferFailure(message) };
        }
        const data = json.data ? normalizeSelf(json.data) : undefined;
        return { ok: true, ...(data ? { data } : {}), ...(json.warning ? { get warning() { return transferMessage(transferRemote(json, "warning")) as string; } } : {}) };
      } catch (err) {
        return { ...transferFailure(err instanceof Error ? err.message : transferLocal("failedToSetTheDashboardURL")) };
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return { saving, setDashboardUrl };
};

/** Fill in defaults for older payloads that predate the auto-advertise fields. */
const normalizeSelf = (data: SelfDataDto): InstanceAdvertiseState => ({
  dashboardUrl: data.dashboardUrl,
  // Server default is TRUE; an absent flag means the box is auto-advertising.
  autoAdvertise: data.autoAdvertise ?? true,
  effectiveSource: data.effectiveSource ?? (data.dashboardUrl ? "manual" : "auto"),
});

type UseAdvertisedDashboardUrlReturn = InstanceAdvertiseState & {
  /** True once the read-back has completed (so the input can move off its placeholder). */
  loaded: boolean;
  refresh: () => void;
};

/**
 * Reads THIS instance's advertise state for the settings prefill: the manual
 * override URL, the auto-advertise flag, and the effective policy branch. A
 * failure (endpoint not yet deployed / transient) leaves the override null,
 * `autoAdvertise` true (the server default), and `loaded` true so the field falls
 * back to an empty, editable input with auto shown on.
 */
export const useAdvertisedDashboardUrl = (): UseAdvertisedDashboardUrlReturn => {
  useTransfersLanguage();
  const [state, setState] = useState<InstanceAdvertiseState>({
    dashboardUrl: null,
    autoAdvertise: true,
    effectiveSource: "auto",
  });
  const [loaded, setLoaded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSelf = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch("/api/transfer/instances/self", { credentials: "include", signal });
      const json = (await res.json().catch(() => null)) as
        | { ok: true; data: SelfDataDto }
        | { ok: false; message?: string; messageFeedback?: TransferFeedback }
        | null;
      if (signal?.aborted) return;
      if (res.ok && json?.ok) {
        setState(normalizeSelf(json.data));
      } else {
        setState({ dashboardUrl: null, autoAdvertise: true, effectiveSource: "auto" });
      }
      setLoaded(true);
    } catch {
      if (signal?.aborted) return;
      setState({ dashboardUrl: null, autoAdvertise: true, effectiveSource: "auto" });
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    void fetchSelf(controller.signal);
    return () => controller.abort();
  }, [fetchSelf]);

  const refresh = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void fetchSelf(controller.signal);
  }, [fetchSelf]);

  return { ...state, loaded, refresh };
};
